/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Collapse from '@mui/material/Collapse';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Col, Row, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import TextInput from 'components/input/textInput';
import SelectInput from 'components/input/SelectInput';
import { classTypeOption, subjectOptions } from 'screen/course/courseConstant';
import AutocompleteInput from 'components/input/AutoCompleteInput';
import { useLocation } from 'react-router-dom';
interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

/** Same options as SubjectConfig "Type of Class" so autofill from 12th-grade courses matches. */
const levelOptions = classTypeOption;

/** Map legacy Course Level labels → current Type of Class labels. */
const mapTypeOfClassToCourseLevel = (typeOfClass?: string) => {
  if (!typeOfClass) return 'Regular/Standard';
  if (classTypeOption.includes(typeOfClass)) return typeOfClass;
  const legacyMap: Record<string, string> = {
    'Advanced Placement': 'Advanced Placement (AP)',
    Regular: 'Regular/Standard',
    'AS/A-Level/International A-Level/Cambridge AICE':
      'AS/A-level/International A-level, Cambridge AICE',
    'GSCE/IGSCE': 'GCSE, IGCSE',
  };
  return legacyMap[typeOfClass] || typeOfClass;
};

interface Props {
  handleSave: (value: any) => void;
  formik: any;
  item: any;  
  index: number;
  total: number;
  expandAll? : boolean;
  handleExpandActive: (value: any) => void;
  isMobile?: boolean;
}

const CourseWork = (props: Props) => {
  const { isMobile = false } = props;
  const [scheduleOptions, setScheduleOptions] = React.useState(['Full Year']);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const check = query.get('check');
  
  const formItem = useFormik({
    initialValues: {
      course_scheduling_system_is_using: '',
      title: '',
      level: 'Regular/Standard',
      schedule: ['Full Year'],
      subject: '',
    },
    onSubmit: (values) => {},
  });
  const [expanded, setExpanded] = React.useState(props.expandAll || false);
  const handleExpandClick = () => {
    setExpanded(!expanded);
    props.handleExpandActive(expanded);
  };
  React.useEffect(() => {
    setExpanded(props.expandAll || false);
  }, [props?.expandAll])

  React.useEffect(() => {
    if (props.item)
      if (!props.item?.course_scheduling_system_is_using){
          props.item.course_scheduling_system_is_using = props.formik.values.course_scheduling_system_is_using

          switch (props.item.course_scheduling_system_is_using) {
            case 'Semester':
              props.item.schedule = ['First Semester', 'Second Semester'];
              break;
            case 'Trimester':
              props.item.schedule = [
                'First Trimester',
                'Second Trimester',
                'Third Trimester'
              ];
              break;
            case 'Quarter':
              props.item.schedule = [
                'First Quarter',
                'Second Quarter',
                'Third Quarter',
                'Fourth Quarter',
              ];
              break;
            case 'Yearly':
              props.item.schedule = ['Full Year'];
              break;
          }
      }

      formItem.resetForm({
        values: {
          ...formItem.values,
          ...props.item,
          level: mapTypeOfClassToCourseLevel(props.item?.level),
          schedule: Array.isArray(props.item?.schedule) ? props.item?.schedule : [],
        },
      });

  
  }, [props.item]);
  
  React.useEffect(() => {
    switch (formItem.values.course_scheduling_system_is_using) {
      case 'Semester':
        setScheduleOptions(['Full Year', 'First Semester', 'Second Semester']);
        break;

      case 'Trimester':
        setScheduleOptions([
          'Full Year',
          'First Trimester',
          'Second Trimester',
          'Third Trimester',
        ]);
        break;
      case 'Quarter':
        setScheduleOptions([
          'Full Year',
          'First Quarter',
          'Second Quarter',
          'Third Quarter',
          'Fourth Quarter',
        ]);
        break;
      default:
        setScheduleOptions(['Full Year']);
        break;
    }

    if (formItem.dirty) {
      props.handleSave({ ...formItem.values });
    }
  }, [formItem.values]);
  return (
    <div className="m-0">
      <Card className="w-100 my-3 px-0">
        <CardActions
          id={`courseWorkItem${props.index}`}
          aria-expanded={expanded}
          disableSpacing
          onClick={handleExpandClick}
          sx={{
            backgroundColor: expanded ? '#AAEBF7' : 'none',
            borderBottom: expanded ? '2px solid #4D88F0' : 'none',
          }}
        >
          <div className="w-100 d-flex justify-content-between">
            <span className="p-1">{formItem.values.title} - {formItem.values.subject} - {formItem.values.schedule.join(', ')}</span>
            <strong className="text-field-light">
              {props.index}/{props.total}
            </strong>
          </div>
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </CardActions>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <Row>
              <SelectInput
                id={`courseworkSchedulingSystem_${props.index}`}
                form={formItem}
                mdCol="12"
                label="Please select the course scheduling system your institution is using.*"
                name="course_scheduling_system_is_using"
                values={formItem.values.course_scheduling_system_is_using}
                onChange={formItem.handleChange}
                option={['Semester', 'Trimester', 'Quarter', 'Yearly', 'Other']}
                isMissing={!formItem.values.course_scheduling_system_is_using && check ? true : false}
              />
              <TextInput
                id={`courseworkTitle_${props.index}`}
                mdCol="12"
                label="Course Title"
                name="title"
                countText={100}
                values={formItem.values.title}
                onChange={formItem.handleChange}
                isMissing={!formItem.values.title && check ? true : false}
              />
              <SelectInput
                id={`courseworkLevel_${props.index}`}
                form={formItem}
                option={levelOptions}
                label="Course Level"
                name="level"
                values={formItem.values.level}
                onChange={formItem.handleChange}
                isMissing={!formItem.values.level && check ? true : false}
              />
              <AutocompleteInput
                id={`courseworkSchedule_${props.index}`}
                multiple={true}
                onChange={(val: any) => {
                  formItem.setFieldValue('schedule', val);
                }}
                option={scheduleOptions}
                label="Course Schedule*"
                name="schedule"
                values={formItem.values.schedule}
                isMissing={(!formItem.values.schedule || (formItem.values.schedule!==null && formItem.values.schedule.length===0)) && check ? true : false}
              />
              <SelectInput
                id={`courseworkSubject_${props.index}`}
                mdCol="12"
                form={formItem}
                option={subjectOptions}
                label="Subject*"
                values={formItem.values.subject}
                name="subject"
                onChange={formItem.handleChange}
                feedback={`Please choose the closest matching name or "Other' if your class is not on the list.`}
                isMissing={(!formItem.values.subject || formItem.values.subject === '--') && check ? true : false}
              />

              <Col xs={6} md={9} />
              <Col xs={6} md={3} className="p-3">
                <Button
                  className="w-100"
                  onClick={() => {
                    props.handleSave(formItem.values);
                    setExpanded(false);
                  }}
                >
                  Save Info
                </Button>
              </Col>
            </Row>
          </CardContent>
        </Collapse>
      </Card>
    </div>
  );
};

export default CourseWork;
